import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { fetchOrgStructure } from '@/components/selects/HrOrgSelects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import {
  BusinessUnitSelect,
  DepartmentSelect,
  DesignationSelect,
  GradeSelect,
  EmployeeSelect,
  EmploymentTypeSelect,
} from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { FormGrid } from '@/components/ui/FormGrid';
import { Modal } from '@/components/ui/Modal';
import type { Employee360Response } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type Props = {
  open: boolean;
  employeeId: number;
  employment?: Employee360Response['employment'];
  workforceEmploymentType?: string;
  onClose: () => void;
  onSaved: () => void;
};

function toSelectValue(id?: number) {
  return id != null ? String(id) : '';
}

function toOptionalId(value: string): number | undefined {
  return value ? Number(value) : undefined;
}

export function HrEmployeeEmploymentEditModal({
  open,
  employeeId,
  employment,
  workforceEmploymentType,
  onClose,
  onSaved,
}: Props) {
  const [departmentId, setDepartmentId] = useState('');
  const [designationId, setDesignationId] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [saving, setSaving] = useState(false);
  const [gradeTouched, setGradeTouched] = useState(false);

  const resetForm = useCallback(() => {
    setDepartmentId(toSelectValue(employment?.departmentId));
    setDesignationId(toSelectValue(employment?.designationId));
    setGradeId(toSelectValue(employment?.gradeId));
    setBusinessUnitId(toSelectValue(employment?.businessUnitId));
    setManagerId(toSelectValue(employment?.managerId));
    setEmploymentType(employment?.employmentType ?? workforceEmploymentType ?? '');
    setGradeTouched(false);
  }, [employment, workforceEmploymentType]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const onDesignationChange = async (value: string) => {
    setDesignationId(value);
    if (!value || gradeTouched) return;
    try {
      const org = await fetchOrgStructure();
      const designation = org.designations.find((d) => String(d.id) === value);
      if (designation?.gradeId) {
        setGradeId(String(designation.gradeId));
      }
    } catch {
      // Grade auto-fill is optional; user can pick manually.
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await hrApi.upsertEmployeeProfile({
        employeeId,
        departmentId: toOptionalId(departmentId),
        designationId: toOptionalId(designationId),
        gradeId: toOptionalId(gradeId),
        businessUnitId: toOptionalId(businessUnitId),
        managerId: toOptionalId(managerId),
        employmentType: employmentType || undefined,
      });
      notify.success('Employment details saved');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save employment details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit employment"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <PageActionGate action="edit">
            <Button loading={saving} onClick={() => void save()}>
              Save
            </Button>
          </PageActionGate>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-500">
        Assign department, designation, grade, and business unit from your company master data. Create masters under{' '}
        <span className="font-medium text-slate-700 dark:text-slate-300">HR → Master data</span> if lists are empty.
      </p>
      <FormGrid columns={2} className="gap-3">
        <BusinessUnitSelect enabled={open} value={businessUnitId} onChange={setBusinessUnitId} />
        <DepartmentSelect enabled={open} value={departmentId} onChange={setDepartmentId} />
        <DesignationSelect enabled={open} value={designationId} onChange={(v) => void onDesignationChange(v)} />
        <GradeSelect
          enabled={open}
          value={gradeId}
          onChange={(v) => {
            setGradeTouched(true);
            setGradeId(v);
          }}
        />
        <EmployeeSelect label="Manager" value={managerId} onChange={setManagerId} hrScoped />
        <div className="col-span-2 sm:col-span-1">
          <EmploymentTypeSelect value={employmentType} onChange={setEmploymentType} />
        </div>
      </FormGrid>
    </Modal>
  );
}
